<template>
  <div class="ai-mapping-list">
    <header>
      <span class="list-heading">{{ $t("ai-sections.ai-variable") }} </span>
      <span class="list-heading">{{ $t("ai-sections.variables") }}</span>
      <span class="list-heading">{{ $t("ai-sections.examples") }}</span>
      <span class="list-heading">{{ $t("ai-sections.activate/deactivate") }}</span>
    </header>

    <main class="mapping-list">
      <AiMappingListInstance
        class="mapping-instance"
        v-for="(mapping, index) in mappings"
        :key="index"
        :mapping="mapping"
        @on-open-variable-list="onOpenVariablePopover"
        @on-format-variable="onOpenFormatPopover"
        @expression-value="
          (expression) => {
            setExpressionValue(mapping, expression);
          }
        "
        @trash-variable="trashVariable(mapping)"
        @remove-selected-variable-object="removeSelectedVariableObject(mapping)"
        @drag-element="(e) => startDrag(e, index)"
        @drop-element="(e) => onDrop(e, index)"
        @on-remove-mapping="onRemoveMapping(index)"
      />

      <div class="add-variable-wrapper">
        <div class="add-variable-btn" @click="onAddMapping" v-if="!newMappingInput">
          <span>
            {{ "+ " + $t("variable") }}
          </span>
        </div>
        <form @submit.prevent.stop="addNewMapping" class="ai-value-input" v-else>
          <input v-model="newMappingValue" ref="mappinginput" placeholder="AI Variable name" />
          <span class="invalid-message text-xs text-danger" v-if="invalidInput" v-html="invalidInputMessage"></span>
          <button type="submit" class="btn btn-small add-variable">{{ $t("add") }}</button>
          <button type="reset" class="btn btn-small" @click="newMappingInput = !newMappingInput">
            <i class="fa-light fa-times"></i>
          </button>
        </form>
      </div>
    </main>
    <mapping-popover
      v-if="showPopover"
      @close="showPopover = false"
      :pos="popoverPosition"
      :expression="currentMappingInstance.expression"
      @add-expression="mapVariable"
    />
    <format-popover
      v-if="showFormatPopover"
      @close="showFormatPopover = false"
      :pos="popoverPosition"
      :expression="currentMappingInstance.expression"
      @update-variable-format="formatCurrentVariable"
    />
  </div>
</template>
<script>
import VariableHelpers from "@/assets/js/variableHelpers.js";
import AiMappingListInstance from "@/components/AiMappingListInstance";
import MappingPopover from "@/components/MappingPopover";
import FormatPopover from "@/components/FormatPopover";
import ConfirmModal from "@/components/modals/ConfirmModal";
export default {
  components: {
    AiMappingListInstance,
    MappingPopover,
    FormatPopover
  },
  props: {
    mappings: Array
  },
  data() {
    return {
      showPopover: false,
      showFormatPopover: false,
      popoverPosition: {},
      currentMappingInstance: null,
      newMappingInput: false,
      newMappingValue: "",
      invalidInput: false
    };
  },
  computed: {
    nameExists() {
      return this.mappings.some((mapping) => mapping.value === this.newMappingValue);
    },
    invalidInputMessage() {
      return this.nameExists
        ? `<em>${this.newMappingValue}</em> ${this.$t("alread-exists")}`
        : this.$t("invalid-default");
    }
  },
  methods: {
    // Drag n drop features
    startDrag(e, index) {
      e.dataTransfer.dropEffect = "move";
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("mappingIndex", index);
    },
    onDrop(e, index) {
      const draggedMappingnIndex = e.dataTransfer.getData("mappingIndex");
      const draggedMapping = this.mappings[draggedMappingnIndex];
      this.mappings.splice(draggedMappingnIndex, 1);
      this.mappings.splice(index, 0, draggedMapping);
    },

    addNewMapping() {
      this.invalidInput = false;
      if (this.newMappingValue.length > 0 && !this.nameExists) {
        this.$emit("add-mapping", this.newMappingValue);
        this.newMappingInput = false;
        this.newMappingValue = "";
      } else {
        this.invalidInput = true;
      }
    },
    onAddMapping() {
      this.invalidInput = false;
      this.newMappingInput = !this.newMappingInput;
      this.$nextTick(() => {
        this.$refs.mappinginput.focus();
      });
    },
    onRemoveMapping(index) {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t("ai-sections.remove-title"),
          description: this.$t("ai-sections.remove-description"),
          onConfirm: () => {
            this.$emit("remove-mapping", index);
          }
        },
        { width: 350, height: "auto" }
      );
    },
    onOpenVariablePopover(e, mapping) {
      this.currentMappingInstance = mapping;
      this.popoverPosition = { x: e.x, y: e.y };
      this.showPopover = true;
    },
    onOpenFormatPopover(e, mapping) {
      this.currentMappingInstance = mapping;
      this.popoverPosition = { x: e.x, y: e.y };
      this.showFormatPopover = true;
    },
    mapVariable(expression) {
      this.$set(this.currentMappingInstance, "expression", expression);
      this.$set(this.currentMappingInstance, "active", true);
      this.showPopover = false;
    },
    formatCurrentVariable(format) {
      if (format) {
        this.$set(
          this.currentMappingInstance,
          "expression",
          VariableHelpers.insertFormatting(
            VariableHelpers.removeFormatting(this.currentMappingInstance.expression),
            format
          )
        );
      }
      // this.$set(this.currentMappingInstance.selectedVariable, "format", format);
    },
    removeSelectedVariableObject(mapping) {
      this.$set(mapping, "selectedVariable", null);
    },
    trashVariable(mapping) {
      this.$set(mapping, "selectedVariable", null);
      this.$set(mapping, "expression", null);
    },
    setExpressionValue(mapping, expression) {
      this.$set(mapping, "expression", expression);
    }
  },
  watch: {
    newMappingValue() {
      this.invalidInput = false;
    }
  }
};
</script>

<style lang="less">
.ai-mapping-list {
  header {
    padding: 0 12px;
    display: grid;
    grid-template-columns: minmax(max-content, 150px) minmax(max-content, 564px) 1fr 100px;
    .list-heading {
      font-size: 14px;
      font-weight: 700;
      &:nth-child(2) {
        margin-left: 158px;
      }
      &:nth-child(3) {
        margin-left: -93px;
      }
    }

    .variable-tip {
      opacity: 0.75;
      &:hover {
        opacity: 1;
      }
    }
  }
  main {
    display: flex;
    flex-direction: column;
    // gap: 14px;
  }

  .ai-value-input {
    opacity: 1;
    padding: 0;
    input {
      background: var(--charcoal-border);
      border: none;
      padding: 8px 12px;
      color: #fff !important;
    }
    button {
      // background: none;
    }
  }

  .add-variable {
    &:hover {
      border: 1px solid #fff;
    }
  }

  .add-variable-wrapper {
    // border:1px solid #ffffff50;
    margin: 12px;
    font-weight: 500;
    color: #ffffff50;

    .add-variable-btn {
      cursor: pointer;
      border: 1px solid #ffffff50;
      min-width: 170px;
      width: max-content;
      padding: 8px 12px;
      border-radius: 4px;
      &:hover {
        color: #ffffff;
        border: 1px solid #fff;
      }
    }
    .ai-value-input {
      position: relative;
      border-radius: 4px;
      // border: 1px solid #fff;
      display: flex;
      gap: 12px;

      .btn {
        color: #ffffff50;
        &:hover,
        &:focus {
          color: #fff;
        }
      }
      .add-variable {
        font-size: 12px;
        padding-left: 18px;
        padding-right: 18px;
        border: 1px solid #ffffff50;
        &:hover {
          border: 1px solid #fff;
        }
      }
      input {
        outline: none;
        flex-grow: 1;
        border-radius: 4px;
        box-shadow: 0 0 4px 2px #13121224;
        &:focus {
          box-shadow: 0 0 4px 3px #00000025;
        }
        &::placeholder {
          color: #ffffff75;
        }
      }
      .invalid-message {
        position: absolute;
        bottom: -18px;
        left: 6px;
      }
    }
  }
}
@media only screen and (max-width: 1450px) {
  .ai-mapping-list {
    header {
      .list-heading {
        &:nth-child(3) {
          margin-left: -145px;
        }
      }
    }
  }
}
@media only screen and (max-width: 1250px) {
  .ai-mapping-list {
    header {
      grid-template-columns: 150px 150px 1fr 150px;
      .list-heading {
        &:nth-child(3) {
          margin: 0px;
        }
        &:nth-child(2) {
          margin-left: -10px;
        }
        &:nth-child(4) {
          justify-self: center;
        }
      }
    }
  }
}
</style>
